import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowDownRight, ArrowUpRight, Lock, Settings2, Sparkles } from 'lucide-react'
import type {
  MetricComparisonKind,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  ServiceLineRollup,
} from '../../../data/ServerApiClient'
import { statementComparisonWindow, statementCurrentIndex, statementRolledValue } from './CfoFinancialsPage'
import { CfoShellSelectChip } from './CfoPageShell'
import { PinToReportButton, type AddToReportPayload } from './presentation/useAddToReport'
import { cfoCurrencySymbol } from './cfoFormat'

// Build the board-pack block for the contribution analysis. Pinned as a text block (always renders in
// the studio + PPTX export) with the structured per-line data kept in `source` for richer rendering.
function serviceLineReportPayload(calc: Calc, periodLabel: string, helper: string): AddToReportPayload {
  const lineText = calc.lines
    .map((l) => `• ${l.name}: ${fmtPct(l.curPct)} CM (${fmtMoney(l.curCm)})`)
    .join('\n')
  const text =
    `Direct contribution margin by service line — ${periodLabel} (${helper}).\n` +
    `${lineText}\n` +
    `Σ Direct contribution: ${fmtPct(calc.total.curPct)} (${fmtMoney(calc.total.curCm)}).\n` +
    `Indirect / shared costs: ${fmtPct(calc.shared.curRatio)} of total sales` +
    `${calc.shared.curAmt != null ? ` (${fmtMoney(Math.abs(calc.shared.curAmt))})` : ''} — not allocated to a line.`
  return {
    title: `Service-line contribution — ${periodLabel}`,
    kind: 'text',
    section: 'profitAndLoss',
    text,
    source: {
      kind: 'service_line_margins',
      period: periodLabel,
      comparison: helper,
      lines: calc.lines.map((l) => ({ name: l.name, cm_pct: l.curPct, cm: l.curCm, prior_cm_pct: l.priorPct })),
      total: { cm_pct: calc.total.curPct, cm: calc.total.curCm, prior_cm_pct: calc.total.priorPct },
      shared: { ratio: calc.shared.curRatio, amount: calc.shared.curAmt, prior_ratio: calc.shared.priorRatio },
    },
  }
}

const TEAL = '#0F6E56'
const RED = '#A32D2D'
const INK = '#111827'
const MUTED = '#6b7280'
const FAINT = '#9ca3af'
const HAIR = '#e5e7eb'

const HELPER_LABEL: Record<MetricComparisonKind, string> = {
  mom: 'Month-over-month',
  qoq: 'Quarter-over-quarter',
  yoy: 'Same month last year',
  ytd: 'Year to date',
  l3m_vs_prior3m: 'Last 3mo vs prior 3mo',
  l12m_vs_prior12m: 'Last 12mo vs prior 12mo',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatMonth(period: string | undefined): string {
  if (!period) return ''
  const m = /^(\d{4})-(\d{2})/.exec(period)
  if (!m) return period
  const monthIdx = Math.max(0, Math.min(11, Number(m[2]) - 1))
  return `${MONTHS[monthIdx]} ${m[1].slice(2)}`
}

function fmtMoney(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value / 1000)
  return `${value < 0 ? '(' : ''}${cfoCurrencySymbol()}${abs.toFixed(1)}K${value < 0 ? ')' : ''}`
}

function fmtPct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(1)}%`
}

type LineCalc = {
  id: string
  name: string
  autoDetected: boolean
  curRev: number | null
  curCogs: number | null
  curCm: number | null
  curPct: number | null
  priorPct: number | null
  priorCm: number | null
  trail: Array<{ idx: number; pct: number | null }>
}

// Indirect / shared-cost pool analyzed over time, as a share of total sales (lower is better).
type SharedCalc = {
  curRatio: number | null
  priorRatio: number | null
  curAmt: number | null
  priorAmt: number | null
  trail: Array<{ idx: number; pct: number | null }>
  gmin: number
  gmax: number
}

// One P&L account's prior→now movement for the "By account" lens — the same
// grid grammar as the segment rows, computed straight from the statement
// render (no rollup configuration required).
type AccountMoveRow = {
  id: string
  label: string
  kind: 'revenue' | 'cogs'
  cur: number | null
  prior: number | null
  curShare: number | null
  priorShare: number | null
}

type AccountCalc = {
  revenue: AccountMoveRow[]
  cogs: AccountMoveRow[]
  totalIncome: number | null
  totalIncomePrior: number | null
  totalCogs: number | null
  totalCogsPrior: number | null
  grossProfit: number | null
  grossProfitPrior: number | null
  gpPct: number | null
  gpPctPrior: number | null
  shareMax: number
  deltaMax: number
  currentIndices: number[]
  priorIndices: number[]
  periods: string[]
}

/** The comparison window shared by both lenses: the displayed span in
 *  "Periods" mode (unless the user overrode the period chip), else the
 *  selected comparison kind's window. */
function comparisonIndices(
  statement: QuickBooksFpaStatementRender,
  endPeriod: string | null | undefined,
  kind: MetricComparisonKind,
  spanMode: boolean,
  periodsCount: number,
): { currentIndices: number[]; priorIndices: number[] } | null {
  const currentIndex = statementCurrentIndex(statement, endPeriod)
  if (currentIndex < 0) return null
  if (spanMode) {
    const count = Math.max(2, Math.min(periodsCount, currentIndex + 1))
    const firstIndex = Math.max(0, currentIndex - (count - 1))
    return { currentIndices: [currentIndex], priorIndices: [firstIndex] }
  }
  const window = statementComparisonWindow(statement, endPeriod, kind)
  return { currentIndices: window.currentIndices, priorIndices: window.priorIndices }
}

const PERIOD_CHIP_OPTIONS = (Object.entries(HELPER_LABEL) as Array<[MetricComparisonKind, string]>).map(
  ([id, label]) => ({ id, label }),
)

const signedMoneyTick = (v: number): string => {
  const abs = Math.abs(v)
  const magnitude = abs >= 950 ? `${cfoCurrencySymbol()}${Math.round(abs / 1000)}K` : `${cfoCurrencySymbol()}${Math.round(abs)}`
  return `${v < 0 ? '−' : v > 0 ? '+' : ''}${magnitude}`
}

type Calc = {
  lines: LineCalc[]
  total: LineCalc
  // Gross-profit margin (gross profit ÷ total sales) as a movement row — the bottom line's own analysis.
  gp: LineCalc
  trackedRev: number | null
  trackedCm: number | null
  sharedCogs: number | null
  otherRevenue: number | null
  grossProfit: number | null
  // Prior-period reconcile values so the whole bridge rolls forward.
  trackedCmPrior: number | null
  otherRevenuePrior: number | null
  sharedCogsPrior: number | null
  grossProfitPrior: number | null
  shared: SharedCalc
  currentIndices: number[]
  priorIndices: number[]
  trailRange: { start: number; end: number }
  gmin: number
  gmax: number
  periods: string[]
}

export type ServiceLineContributionPanelProps = {
  statement: QuickBooksFpaStatementRender | null
  endPeriod: string | null | undefined
  comparisonKind: MetricComparisonKind
  // When the financials are in "Periods" (multi-column) mode, the panel spans the displayed range:
  // first displayed period → last displayed period, with the trend across all of them.
  periodsMode?: boolean
  periodsCount?: number
  rollups: ServiceLineRollup[]
  seeded: boolean
  suggestionsCount: number
  loading?: boolean
  detecting?: boolean
  onDetect: () => void
  onConfigure: () => void
  onReviewSuggestions: () => void
}

export function ServiceLineContributionPanel({
  statement,
  endPeriod,
  comparisonKind,
  periodsMode = false,
  periodsCount = 12,
  rollups,
  seeded,
  suggestionsCount,
  loading = false,
  detecting = false,
  onDetect,
  onConfigure,
  onReviewSuggestions,
}: ServiceLineContributionPanelProps) {
  const [view, setView] = useState<'agg' | 'trail'>('agg')
  const [val, setVal] = useState<'pct' | 'usd'>('pct')
  // Account-lens value mode: dollars by default (accounts are read in $),
  // share-of-revenue as the opt-in view.
  const [accountVal, setAccountVal] = useState<'usd' | 'pct'>('usd')
  // Lens: segment CM (needs configured rollups) vs raw account movement
  // (always available from the statement). No rollups → accounts, always.
  const [lens, setLens] = useState<'segments' | 'accounts'>('segments')
  const effectiveLens: 'segments' | 'accounts' = rollups.length === 0 ? 'accounts' : lens
  // In-panel period override; null follows the page-level comparison. Resets
  // when the page comparison changes so the panel tracks the page again.
  const [periodOverride, setPeriodOverride] = useState<MetricComparisonKind | null>(null)
  useEffect(() => {
    setPeriodOverride(null)
  }, [comparisonKind, periodsMode])
  const effectiveKind: MetricComparisonKind = periodOverride ?? comparisonKind
  // "Periods" span mode holds only until the user picks an explicit window.
  const spanMode = periodsMode && !periodOverride

  const calc = useMemo<Calc | null>(() => {
    if (!statement || statement.periods.length === 0 || rollups.length === 0) return null
    const rowByNode = new Map<string, QuickBooksFpaStatementRow>()
    for (const row of statement.rows) rowByNode.set(row.node_id, row)

    const nodeSum = (nodeIds: string[], idx: number): number | null => {
      let total = 0
      let found = false
      for (const id of nodeIds) {
        const row = rowByNode.get(id)
        if (!row) continue
        const value = statementRolledValue(statement, row, idx)
        if (value != null) {
          total += value
          found = true
        }
      }
      return found ? total : null
    }
    const windowSum = (nodeIds: string[], indices: number[]): number | null => {
      let total = 0
      let found = false
      for (const idx of indices) {
        const value = nodeSum(nodeIds, idx)
        if (value != null) {
          total += value
          found = true
        }
      }
      return found ? total : null
    }
    const cmPct = (rev: number | null, cogs: number | null): number | null =>
      rev != null && Math.abs(rev) > 0.005 ? (rev - (cogs || 0)) / rev : null

    const currentIndex = statementCurrentIndex(statement, endPeriod)
    if (currentIndex < 0) return null
    // In "Periods" mode the window spans the displayed range: first displayed period (prior) → last
    // displayed period (now), with the trend across all displayed periods. Otherwise use the selected
    // comparison's window (mom / qoq / yoy / rolling) — the in-panel period chip can override the page.
    const win = comparisonIndices(statement, endPeriod, effectiveKind, spanMode, periodsCount)
    if (!win) return null
    const { currentIndices, priorIndices } = win
    if (currentIndices.length === 0) return null
    const trailStart = Math.min(priorIndices[0] ?? currentIndex, currentIndices[0] ?? currentIndex)
    const trailEnd = currentIndices[currentIndices.length - 1] ?? currentIndex

    const sectionRow = (re: RegExp): QuickBooksFpaStatementRow | null =>
      statement.rows.find(
        (r) => (r.is_total || r.row_type === 'group' || r.row_type === 'heading') && re.test(String(r.label || '')),
      ) || null
    const totalIncomeRow = sectionRow(/^(total\s+)?income$/i) || sectionRow(/income/i)
    const totalCogsRow = sectionRow(/cost of (goods|sales)/i)
    const sectionWindow = (row: QuickBooksFpaStatementRow | null): number | null => {
      if (!row) return null
      let total = 0
      let found = false
      for (const idx of currentIndices) {
        const v = statementRolledValue(statement, row, idx)
        if (v != null) {
          total += v
          found = true
        }
      }
      return found ? total : null
    }

    const lines: LineCalc[] = rollups.map((rollup) => {
      const curRev = windowSum(rollup.income_node_ids, currentIndices)
      const curCogs = windowSum(rollup.cogs_node_ids, currentIndices)
      const priorRev = windowSum(rollup.income_node_ids, priorIndices)
      const priorCogs = windowSum(rollup.cogs_node_ids, priorIndices)
      const trail: Array<{ idx: number; pct: number | null }> = []
      for (let idx = trailStart; idx <= trailEnd; idx += 1) {
        trail.push({ idx, pct: cmPct(nodeSum(rollup.income_node_ids, idx), nodeSum(rollup.cogs_node_ids, idx)) })
      }
      return {
        id: rollup.id,
        name: rollup.name,
        autoDetected: rollup.auto_detected,
        curRev,
        curCogs,
        curCm: curRev != null ? curRev - (curCogs || 0) : null,
        curPct: cmPct(curRev, curCogs),
        priorPct: cmPct(priorRev, priorCogs),
        priorCm: priorRev != null ? priorRev - (priorCogs || 0) : null,
        trail,
      }
    })

    const sumDefined = (vals: Array<number | null>): number | null => {
      let total = 0
      let found = false
      for (const v of vals) if (v != null) { total += v; found = true }
      return found ? total : null
    }
    const trackedRev = sumDefined(lines.map((l) => l.curRev))
    const trackedCm = sumDefined(lines.map((l) => l.curCm))
    const trackedCogs = sumDefined(lines.map((l) => l.curCogs))
    const totalIncome = sectionWindow(totalIncomeRow)
    const totalCogs = sectionWindow(totalCogsRow)
    const sharedCogs = totalCogs != null && trackedCogs != null ? totalCogs - trackedCogs : null
    const otherRevenue = totalIncome != null && trackedRev != null ? totalIncome - trackedRev : null
    const grossProfit = totalIncome != null && totalCogs != null ? totalIncome - totalCogs : null

    // Indirect / shared-cost pool analyzed as a percent of total sales, rolled forward prior → now.
    const sectionAt = (row: QuickBooksFpaStatementRow | null, idx: number): number | null =>
      row ? statementRolledValue(statement, row, idx) : null
    const sectionWindowFor = (row: QuickBooksFpaStatementRow | null, indices: number[]): number | null => {
      let total = 0
      let found = false
      for (const idx of indices) {
        const v = sectionAt(row, idx)
        if (v != null) { total += v; found = true }
      }
      return found ? total : null
    }
    const ratio = (cost: number | null, sales: number | null): number | null =>
      cost != null && sales != null && Math.abs(sales) > 0.005 ? cost / sales : null
    const totalIncomePrior = sectionWindowFor(totalIncomeRow, priorIndices)
    const totalCogsPrior = sectionWindowFor(totalCogsRow, priorIndices)
    const trackedCogsPrior = sumDefined(rollups.map((r) => windowSum(r.cogs_node_ids, priorIndices)))
    const sharedCogsPrior =
      totalCogsPrior != null && trackedCogsPrior != null ? totalCogsPrior - trackedCogsPrior : null
    const sharedTrail: Array<{ idx: number; pct: number | null }> = []
    for (let idx = trailStart; idx <= trailEnd; idx += 1) {
      const tc = sectionAt(totalCogsRow, idx)
      const ti = sectionAt(totalIncomeRow, idx)
      const trackedC = sumDefined(rollups.map((r) => nodeSum(r.cogs_node_ids, idx)))
      const sh = tc != null && trackedC != null ? tc - trackedC : null
      sharedTrail.push({ idx, pct: ratio(sh, ti) })
    }
    let smin = Infinity
    let smax = -Infinity
    for (const p of sharedTrail) if (p.pct != null) { smin = Math.min(smin, p.pct); smax = Math.max(smax, p.pct) }
    if (!Number.isFinite(smin) || !Number.isFinite(smax) || smin === smax) { smin = 0; smax = 0.5 }
    else { const pad = (smax - smin) * 0.2; smin = Math.max(0, smin - pad); smax += pad }
    const shared: SharedCalc = {
      curRatio: ratio(sharedCogs, totalIncome),
      priorRatio: ratio(sharedCogsPrior, totalIncomePrior),
      curAmt: sharedCogs,
      priorAmt: sharedCogsPrior,
      trail: sharedTrail,
      gmin: smin,
      gmax: smax,
    }

    // Blended Σ Direct contribution, rolled forward prior → now (dollar-weighted across the tracked lines).
    const totalPriorRev = sumDefined(rollups.map((r) => windowSum(r.income_node_ids, priorIndices)))
    const totalPriorCm = sumDefined(lines.map((l) => l.priorCm))
    const totalTrail: Array<{ idx: number; pct: number | null }> = []
    for (let idx = trailStart; idx <= trailEnd; idx += 1) {
      const rev = sumDefined(rollups.map((r) => nodeSum(r.income_node_ids, idx)))
      const cogs = sumDefined(rollups.map((r) => nodeSum(r.cogs_node_ids, idx)))
      totalTrail.push({ idx, pct: cmPct(rev, cogs) })
    }
    // Prior-period reconcile values + the gross-profit margin movement row.
    const grossProfitPrior =
      totalIncomePrior != null && totalCogsPrior != null ? totalIncomePrior - totalCogsPrior : null
    const otherRevenuePrior =
      totalIncomePrior != null && totalPriorRev != null ? totalIncomePrior - totalPriorRev : null
    const gpTrail: Array<{ idx: number; pct: number | null }> = []
    for (let idx = trailStart; idx <= trailEnd; idx += 1) {
      const ti = sectionAt(totalIncomeRow, idx)
      const tc = sectionAt(totalCogsRow, idx)
      const gp = ti != null && tc != null ? ti - tc : null
      gpTrail.push({ idx, pct: ratio(gp, ti) })
    }
    const gp: LineCalc = {
      id: 'grossprofit',
      name: 'Gross profit margin',
      autoDetected: false,
      curRev: totalIncome,
      curCogs: totalCogs,
      curCm: grossProfit,
      curPct: ratio(grossProfit, totalIncome),
      priorPct: ratio(grossProfitPrior, totalIncomePrior),
      priorCm: grossProfitPrior,
      trail: gpTrail,
    }

    const total: LineCalc = {
      id: 'total',
      name: 'Σ Direct contribution',
      autoDetected: false,
      curRev: trackedRev,
      curCogs: trackedCogs,
      curCm: trackedCm,
      curPct: cmPct(trackedRev, trackedCogs),
      priorPct: cmPct(totalPriorRev, trackedCogsPrior),
      priorCm: totalPriorCm,
      trail: totalTrail,
    }

    let gmin = Infinity
    let gmax = -Infinity
    for (const line of [...lines, total, gp]) for (const p of line.trail) if (p.pct != null) {
      gmin = Math.min(gmin, p.pct)
      gmax = Math.max(gmax, p.pct)
    }
    if (!Number.isFinite(gmin) || !Number.isFinite(gmax) || gmin === gmax) {
      gmin = 0
      gmax = 1
    } else {
      const pad = (gmax - gmin) * 0.12
      gmin -= pad
      gmax += pad
    }

    return {
      lines,
      total,
      gp,
      trackedRev,
      trackedCm,
      sharedCogs,
      otherRevenue,
      grossProfit,
      trackedCmPrior: totalPriorCm,
      otherRevenuePrior,
      sharedCogsPrior,
      grossProfitPrior,
      shared,
      currentIndices,
      priorIndices,
      trailRange: { start: trailStart, end: trailEnd },
      gmin,
      gmax,
      periods: statement.periods,
    }
  }, [statement, rollups, effectiveKind, spanMode, endPeriod, periodsCount])

  // Account-movement lens: leaf revenue + COGS accounts from the statement,
  // prior→now over the same window, ranked by movement. Works with zero
  // rollups configured — the gross-margin story never dead-ends.
  const accountCalc = useMemo<AccountCalc | null>(() => {
    if (!statement || statement.periods.length === 0) return null
    const win = comparisonIndices(statement, endPeriod, effectiveKind, spanMode, periodsCount)
    if (!win || win.currentIndices.length === 0) return null
    const { currentIndices, priorIndices } = win
    const pnlRows = statement.rows.filter((row) => !row.statement_type || row.statement_type === 'profit_and_loss')
    const rowByNode = new Map(pnlRows.map((row) => [row.node_id, row]))
    const sectionRow = (re: RegExp): QuickBooksFpaStatementRow | null =>
      pnlRows.find(
        (r) => (r.is_total || r.row_type === 'group' || r.row_type === 'heading') && re.test(String(r.label || '')),
      ) || null
    const totalIncomeRow = sectionRow(/^(total\s+)?income$/i) || sectionRow(/income/i)
    const totalCogsRow = sectionRow(/cost of (goods|sales)/i)
    const inSection = (row: QuickBooksFpaStatementRow, sectionNodeId: string): boolean => {
      let cursor: QuickBooksFpaStatementRow | undefined = row
      for (let hops = 0; hops < 8 && cursor; hops++) {
        if (cursor.node_id === sectionNodeId) return true
        cursor = cursor.parent_node_id ? rowByNode.get(cursor.parent_node_id) : undefined
      }
      return false
    }
    // QBO account-type/classification first (matches the drilldown recipes),
    // section ancestry as the fallback for custom layouts without QBO fields.
    const classify = (row: QuickBooksFpaStatementRow): 'revenue' | 'cogs' | null => {
      const type = String(row.qbo_account_type || '').toLowerCase()
      const cls = String(row.qbo_classification || '').toLowerCase()
      if (type.includes('cost of goods') || type.includes('cogs')) return 'cogs'
      if (type.includes('other income') || type.includes('other expense')) return null
      if (cls === 'revenue' || type.includes('income') || type.includes('revenue')) return 'revenue'
      if (cls === 'expense' || type.includes('expense')) return null
      if (totalCogsRow && inSection(row, totalCogsRow.node_id)) return 'cogs'
      if (totalIncomeRow && inSection(row, totalIncomeRow.node_id)) return 'revenue'
      return null
    }
    const windowVal = (row: QuickBooksFpaStatementRow | null, indices: number[]): number | null => {
      if (!row) return null
      let total = 0
      let found = false
      for (const idx of indices) {
        const value = statementRolledValue(statement, row, idx)
        if (value != null) {
          total += value
          found = true
        }
      }
      return found ? total : null
    }
    const leaves = pnlRows.filter((row) => String(row.account_id || '').trim() && !row.is_total && !row.is_formula)
    const collected: AccountMoveRow[] = []
    for (const row of leaves) {
      const kind = classify(row)
      if (!kind) continue
      const cur = windowVal(row, currentIndices)
      const prior = windowVal(row, priorIndices)
      if ((cur == null || Math.abs(cur) <= 0.005) && (prior == null || Math.abs(prior) <= 0.005)) continue
      collected.push({
        id: row.node_id,
        label: String(row.label || row.account_id || '').trim() || 'Account',
        kind,
        cur,
        prior,
        curShare: null,
        priorShare: null,
      })
    }
    if (!collected.length) return null
    const sumKind = (kind: 'revenue' | 'cogs', pick: (r: AccountMoveRow) => number | null): number | null => {
      let total = 0
      let found = false
      for (const row of collected) {
        if (row.kind !== kind) continue
        const value = pick(row)
        if (value != null) {
          total += value
          found = true
        }
      }
      return found ? total : null
    }
    const totalIncome = windowVal(totalIncomeRow, currentIndices) ?? sumKind('revenue', (r) => r.cur)
    const totalIncomePrior = windowVal(totalIncomeRow, priorIndices) ?? sumKind('revenue', (r) => r.prior)
    const totalCogs = windowVal(totalCogsRow, currentIndices) ?? sumKind('cogs', (r) => r.cur)
    const totalCogsPrior = windowVal(totalCogsRow, priorIndices) ?? sumKind('cogs', (r) => r.prior)
    const share = (value: number | null, sales: number | null): number | null =>
      value != null && sales != null && Math.abs(sales) > 0.005 ? value / sales : null
    for (const row of collected) {
      row.curShare = share(row.cur, totalIncome)
      row.priorShare = share(row.prior, totalIncomePrior)
    }
    // Top movers per side; the tail rolls into an honest remainder row so the
    // lists still foot to the section totals.
    const takeTop = (kind: 'revenue' | 'cogs'): AccountMoveRow[] => {
      const rows = collected
        .filter((row) => row.kind === kind)
        .sort((a, b) => Math.abs((b.cur ?? 0) - (b.prior ?? 0)) - Math.abs((a.cur ?? 0) - (a.prior ?? 0)))
      const top = rows.slice(0, 5)
      const rest = rows.slice(5)
      if (rest.length) {
        const cur = rest.reduce((sum, row) => sum + (row.cur ?? 0), 0)
        const prior = rest.reduce((sum, row) => sum + (row.prior ?? 0), 0)
        if (Math.abs(cur) > 0.005 || Math.abs(prior) > 0.005) {
          top.push({
            id: `${kind}:other`,
            label: `Other ${kind === 'revenue' ? 'income' : 'COGS'} accounts (${rest.length})`,
            kind,
            cur,
            prior,
            curShare: share(cur, totalIncome),
            priorShare: share(prior, totalIncomePrior),
          })
        }
      }
      return top
    }
    const revenue = takeTop('revenue')
    const cogs = takeTop('cogs')
    const grossProfit = totalIncome != null && totalCogs != null ? totalIncome - totalCogs : null
    const grossProfitPrior = totalIncomePrior != null && totalCogsPrior != null ? totalIncomePrior - totalCogsPrior : null
    const gpPct = share(grossProfit, totalIncome)
    const gpPctPrior = share(grossProfitPrior, totalIncomePrior)
    // One axis for every dot in the lens — account shares AND the gross-profit
    // margin — so all dumbbells are directly comparable.
    let shareMax = 0.1
    for (const row of [...revenue, ...cogs]) {
      for (const value of [row.curShare, row.priorShare]) {
        if (value != null && Number.isFinite(value)) shareMax = Math.max(shareMax, Math.abs(value))
      }
    }
    for (const value of [gpPct, gpPctPrior]) {
      if (value != null && Number.isFinite(value)) shareMax = Math.max(shareMax, Math.abs(value))
    }
    shareMax = Math.min(1.05, shareMax * 1.15)
    // Dollar mode plots the CHANGE on a diverging axis ($0 centered, gains
    // right, declines left) — so the shared bound is the largest |Δ| across
    // account rows and the gross-profit line.
    let deltaMax = 1
    for (const row of [...revenue, ...cogs]) {
      if (row.cur != null && row.prior != null) deltaMax = Math.max(deltaMax, Math.abs(row.cur - row.prior))
    }
    if (grossProfit != null && grossProfitPrior != null) {
      deltaMax = Math.max(deltaMax, Math.abs(grossProfit - grossProfitPrior))
    }
    deltaMax *= 1.05
    return {
      revenue,
      cogs,
      totalIncome,
      totalIncomePrior,
      totalCogs,
      totalCogsPrior,
      grossProfit,
      grossProfitPrior,
      gpPct,
      gpPctPrior,
      shareMax,
      deltaMax,
      currentIndices,
      priorIndices,
      periods: statement.periods,
    }
  }, [statement, effectiveKind, spanMode, endPeriod, periodsCount])

  // Periods mode reads best as a trend across the displayed range; default to the trail view on entry.
  useEffect(() => {
    setView(periodsMode ? 'trail' : 'agg')
  }, [periodsMode])

  if (!statement) return null

  // Title + subtitle on their own row; every control lives on the row below
  // so the chips never squeeze the title into a narrow column.
  const header = (
    <div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>
          {effectiveLens === 'accounts' ? 'Gross profit movement' : 'Service-line contribution'}
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 1 }}>
          {effectiveLens === 'accounts'
            ? 'Account-level revenue and COGS movement — how the period built gross profit.'
            : 'Direct COGS as a share of its own revenue line — shared costs held below, never allocated.'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {rollups.length > 0 && accountCalc ? (
          <CfoShellSelectChip className="cfo-financials-select-chip" label="Lens" ariaLabel="Breakdown lens" value={effectiveLens}
            options={[{ id: 'segments', label: 'By segment' }, { id: 'accounts', label: 'By account' }]}
            onChange={(v) => setLens(v === 'accounts' ? 'accounts' : 'segments')} />
        ) : null}
        <CfoShellSelectChip className="cfo-financials-select-chip" label="Period" ariaLabel="Comparison period"
          value={spanMode ? 'span' : effectiveKind}
          options={spanMode ? [{ id: 'span', label: 'Displayed range' }, ...PERIOD_CHIP_OPTIONS] : PERIOD_CHIP_OPTIONS}
          onChange={(v) => setPeriodOverride(v === 'span' ? null : (v as MetricComparisonKind))} />
        {effectiveLens === 'segments' && rollups.length > 0 ? (
          <>
            <CfoShellSelectChip className="cfo-financials-select-chip" label="View" ariaLabel="Contribution view" value={view}
              options={[{ id: 'agg', label: 'Aggregate' }, { id: 'trail', label: 'Per-period trail' }]}
              onChange={(v) => setView(v as 'agg' | 'trail')} />
            <CfoShellSelectChip className="cfo-financials-select-chip" label="Value" ariaLabel="Value mode" value={val}
              options={[{ id: 'pct', label: 'CM %' }, { id: 'usd', label: 'CM amount' }]}
              onChange={(v) => setVal(v as 'pct' | 'usd')} />
          </>
        ) : null}
        {effectiveLens === 'accounts' ? (
          <CfoShellSelectChip className="cfo-financials-select-chip" label="Value" ariaLabel="Account value mode" value={accountVal}
            options={[{ id: 'usd', label: 'Dollars' }, { id: 'pct', label: '% of revenue' }]}
            onChange={(v) => setAccountVal(v === 'pct' ? 'pct' : 'usd')} />
        ) : null}
        <button type="button" className="cfo-shell-chip" onClick={onConfigure} title="Configure service lines"
          style={{ cursor: 'pointer', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>
          <Settings2 size={15} aria-hidden="true" /> Configure
        </button>
        {effectiveLens === 'segments' && calc ? (
          <span style={{ flexShrink: 0, whiteSpace: 'nowrap', display: 'inline-flex' }}>
            <PinToReportButton buildPayload={() => {
              const idxs = calc.currentIndices
              const label = formatMonth(calc.periods[idxs[0]]) + (idxs.length > 1 ? `–${formatMonth(calc.periods[idxs[idxs.length - 1]])}` : '')
              return serviceLineReportPayload(calc, label, HELPER_LABEL[effectiveKind])
            }} />
          </span>
        ) : null}
      </div>
    </div>
  )

  // Account lens (chosen, or forced when no rollups are configured): the
  // segment-CM grid grammar applied to raw P&L accounts — revenue and COGS
  // movers prior→now, reconciling to gross profit.
  if (effectiveLens === 'accounts' && accountCalc) {
    return (
      <section style={panelShell}>
        {header}
        {rollups.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '10px 0 0', padding: '8px 10px', background: '#f6f7f9', borderRadius: 8, fontSize: 12, color: MUTED }}>
            <Sparkles size={13} aria-hidden="true" style={{ color: TEAL, flexShrink: 0 }} />
            {detecting ? (
              <span>Scanning the chart of accounts for service lines…</span>
            ) : suggestionsCount > 0 ? (
              <>
                <span>Detected {suggestionsCount} service {suggestionsCount === 1 ? 'line' : 'lines'} — apply them to unlock the segment lens.</span>
                <button type="button" className="btn" onClick={onReviewSuggestions} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  Review &amp; apply
                </button>
              </>
            ) : (
              <>
                <span>Configure service lines to split this by segment.</span>
                <button type="button" className="btn" onClick={onDetect} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  Detect service lines
                </button>
              </>
            )}
          </div>
        ) : null}
        <AccountLensBody calc={accountCalc} val={accountVal} spanMode={spanMode} helper={spanMode ? 'Displayed range' : HELPER_LABEL[effectiveKind]} />
      </section>
    )
  }

  if (rollups.length === 0) {
    if (!detecting && suggestionsCount === 0) {
      return (
        <section style={{ ...panelShell, padding: '12px 14px' }}>
          <button
            type="button"
            className="btn"
            onClick={onConfigure}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Settings2 size={14} aria-hidden="true" /> Configure service lines
          </button>
        </section>
      )
    }

    return (
      <section style={panelShell}>
        {header}
        <div style={{ padding: '18px 4px', textAlign: 'center', color: MUTED, fontSize: 13 }}>
          {detecting ? (
            <>
              <Sparkles size={18} aria-hidden="true" style={{ color: TEAL, marginBottom: 6 }} />
              <div style={{ color: INK, fontWeight: 500 }}>Scanning the chart of accounts…</div>
              <div>Pairing each revenue line with its directly-attributable COGS.</div>
            </>
          ) : suggestionsCount > 0 ? (
            <>
              <Sparkles size={18} aria-hidden="true" style={{ color: TEAL, marginBottom: 6 }} />
              <div style={{ color: INK, fontWeight: 500, marginBottom: 2 }}>
                Detected {suggestionsCount} service {suggestionsCount === 1 ? 'line' : 'lines'} in this P&amp;L
              </div>
              <div style={{ marginBottom: 12 }}>Each pairs a revenue line with its directly-attributable COGS.</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button type="button" className="btn primary" onClick={onReviewSuggestions} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Sparkles size={14} aria-hidden="true" /> Review &amp; apply
                </button>
                <button type="button" className="btn" onClick={onConfigure} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Settings2 size={14} aria-hidden="true" /> Configure manually
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ color: INK, fontWeight: 500, marginBottom: 2 }}>No service lines configured</div>
              <div style={{ marginBottom: 12 }}>Auto-detect pairs each revenue line with its direct COGS, or set them up by hand.</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button type="button" className="btn primary" onClick={onDetect} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Sparkles size={14} aria-hidden="true" /> Detect service lines
                </button>
                <button type="button" className="btn" onClick={onConfigure} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Settings2 size={14} aria-hidden="true" /> Configure manually
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    )
  }

  if (!calc) {
    return (
      <section style={panelShell}>
        {header}
        <div style={{ padding: 16, color: MUTED, fontSize: 13 }}>
          {loading ? 'Computing contribution margins…' : 'No statement data for the selected period.'}
        </div>
      </section>
    )
  }

  const rangeLabel = (indices: number[]): string =>
    formatMonth(calc.periods[indices[0]]) + (indices.length > 1 ? `–${formatMonth(calc.periods[indices[indices.length - 1]])}` : '')
  const priorLabel = rangeLabel(calc.priorIndices)
  const nowLabel = rangeLabel(calc.currentIndices)
  const periodsShown = calc.trailRange.end - calc.trailRange.start + 1
  const priorHeader = spanMode ? 'First' : 'Prior'
  const nowHeader = spanMode ? 'Latest' : 'Now'

  // One shared, data-driven dot axis for the whole panel: every dumbbell plots
  // a percent-of-revenue-family value (CM%, shared-cost ratio, GM%) on the
  // scale labeled in the column header — no more fixed 30–90% window that
  // piled small or negative CM at the left edge with no explanation.
  const dotValues: number[] = []
  for (const line of [...calc.lines, calc.total, calc.gp]) {
    for (const value of [line.priorPct, line.curPct]) {
      if (value != null && Number.isFinite(value)) dotValues.push(value)
    }
  }
  for (const value of [calc.shared.priorRatio, calc.shared.curRatio]) {
    if (value != null && Number.isFinite(value)) dotValues.push(value)
  }
  let scaleLo = dotValues.length ? Math.min(0, ...dotValues) : 0
  let scaleHi = dotValues.length ? Math.max(...dotValues) : 1
  if (scaleHi <= scaleLo) scaleHi = scaleLo + 1
  const scalePad = (scaleHi - scaleLo) * 0.06
  if (scaleLo < 0) scaleLo -= scalePad
  scaleHi += scalePad

  // CM $ mode plots the CHANGE on a diverging axis ($0 centered, gains right,
  // declines left) — same grammar as the account lens. Shared ±bound across
  // the lines, Σ total, shared-cost pool, and gross profit.
  let cmDeltaMax = 1
  for (const line of [...calc.lines, calc.total]) {
    if (line.curCm != null && line.priorCm != null) cmDeltaMax = Math.max(cmDeltaMax, Math.abs(line.curCm - line.priorCm))
  }
  if (calc.shared.curAmt != null && calc.shared.priorAmt != null) {
    cmDeltaMax = Math.max(cmDeltaMax, Math.abs(calc.shared.curAmt - calc.shared.priorAmt))
  }
  if (calc.grossProfit != null && calc.grossProfitPrior != null) {
    cmDeltaMax = Math.max(cmDeltaMax, Math.abs(calc.grossProfit - calc.grossProfitPrior))
  }
  cmDeltaMax *= 1.05

  return (
    <section style={panelShell}>
      {header}
      <div style={{ fontSize: 12, color: FAINT, margin: '8px 0 4px' }}>
        {spanMode
          ? `Trend across ${periodsShown} periods · ${priorLabel} → ${nowLabel} · dollar-weighted CM%`
          : `${HELPER_LABEL[effectiveKind]} · ${nowLabel} vs ${priorLabel} · ${val === 'usd' && view !== 'trail' ? `bars plot the CM ${cfoCurrencySymbol()} change — ${cfoCurrencySymbol()}0 centered, gains right, declines left` : 'windowed CM% is dollar-weighted'}`}
      </div>

      <div style={gridRow(11.5, FAINT)}>
        <div>Line</div>
        <div style={{ textAlign: 'right' }}>{priorHeader} · {priorLabel}</div>
        {view === 'trail' ? (
          <div style={{ textAlign: 'center' }}>Monthly CM% → now</div>
        ) : val === 'usd' ? (
          <ScaleRuler lo={-cmDeltaMax} hi={cmDeltaMax} label={`CM ${cfoCurrencySymbol()} change`} fmt={signedMoneyTick} />
        ) : (
          <ScaleRuler lo={scaleLo} hi={scaleHi} label="CM % movement" />
        )}
        <div>{nowHeader} · {nowLabel}</div>
      </div>

      {calc.lines.map((line) => (
        <LineRow key={line.id} line={line} val={val} view={view} calc={calc} scaleLo={scaleLo} scaleHi={scaleHi} deltaMax={cmDeltaMax} />
      ))}

      <LineRow line={calc.total} val={val} view={view} calc={calc} scaleLo={scaleLo} scaleHi={scaleHi} deltaMax={cmDeltaMax} emphasis />

      <SharedRow calc={calc} val={val} view={view} scaleLo={scaleLo} scaleHi={scaleHi} deltaMax={cmDeltaMax} />

      {calc.grossProfit != null ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', fontSize: 11.5, color: FAINT, padding: '6px 0', borderTop: `1px solid ${HAIR}` }}>
            <div>Reconciliation</div>
            <div style={{ textAlign: 'right' }}>{priorHeader} · {priorLabel}</div>
            <div />
            <div style={{ textAlign: 'right' }}>{nowHeader} · {nowLabel}</div>
          </div>
          <ReconcileGridRow label="Σ Direct contribution" prior={calc.trackedCmPrior} now={calc.trackedCm} />
          <ReconcileGridRow label="+ Other / unpaired revenue" prior={calc.otherRevenuePrior} now={calc.otherRevenue} />
          <ReconcileGridRow
            label="− Practice-level shared COGS"
            prior={calc.sharedCogsPrior != null ? -calc.sharedCogsPrior : null}
            now={calc.sharedCogs != null ? -calc.sharedCogs : null}
          />
          <ReconcileGridRow
            label="= Gross profit"
            prior={calc.grossProfitPrior}
            now={calc.grossProfit}
            priorPct={calc.gp.priorPct}
            nowPct={calc.gp.curPct}
            calc={calc}
            view={view}
            val={val}
            scaleLo={scaleLo}
            scaleHi={scaleHi}
            deltaMax={cmDeltaMax}
            bold
          />
        </div>
      ) : null}

      <ContributionWaterfall calc={calc} priorPeriodLabel={priorLabel} currentPeriodLabel={nowLabel} />
    </section>
  )
}

function gridRow(fontSize: number, color: string): CSSProperties {
  return { display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, fontSize, color, padding: '6px 0', borderBottom: `1px solid ${HAIR}`, alignItems: 'center' }
}

// One account's prior→now movement: dollars at the ends. In $ mode the track
// is a diverging change bar ($0 centered, gains right, declines left); in %
// mode it's the share-of-revenue dumbbell. COGS coloring is inverted — a
// falling COGS account is good for gross profit.
function AccountMovementRow({ row, hi, val }: { row: AccountMoveRow; hi: number; val: 'usd' | 'pct' }) {
  const delta = row.cur != null && row.prior != null ? row.cur - row.prior : null
  const improved = row.kind === 'cogs' ? (delta ?? 0) <= 0 : (delta ?? 0) >= 0
  const color = improved ? TEAL : RED
  const rose = (delta ?? 0) >= 0
  const deltaText = delta == null ? '—' : `${delta >= 0 ? '+' : '−'}${cfoCurrencySymbol()}${Math.abs(delta / 1000).toFixed(1)}K`
  const sharePts =
    row.curShare != null && row.priorShare != null ? (Math.abs(row.curShare) - Math.abs(row.priorShare)) * 100 : null
  const cell = (value: number | null, shareValue: number | null, c: string, muted?: boolean) => (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: muted ? MUTED : c }}>{fmtMoney(value)}</div>
      <div style={{ fontSize: 11, color: FAINT }}>{shareValue != null ? `${fmtPct(shareValue)} of rev` : ' '}</div>
    </div>
  )
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${HAIR}` }}>
      <div style={{ fontWeight: 500, fontSize: 13.5, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.label}</div>
      {cell(row.prior, row.priorShare, MUTED, true)}
      <div>
        <div style={{ textAlign: 'center', fontSize: 11.5, color, marginBottom: 1 }}>
          {rose ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />}{' '}
          {deltaText}
          {sharePts != null ? ` · ${sharePts >= 0 ? '+' : '−'}${Math.abs(sharePts).toFixed(1)}pts` : ''}
        </div>
        {val === 'usd' ? (
          <DivergingDeltaBar delta={delta} max={hi} improved={improved} />
        ) : (
          <Dumbbell
            prior={row.priorShare != null ? Math.abs(row.priorShare) : null}
            now={row.curShare != null ? Math.abs(row.curShare) : null}
            up={improved}
            lo={0}
            hi={hi}
          />
        )}
      </div>
      {cell(row.cur, row.curShare, color)}
    </div>
  )
}

// The "By account" lens body: revenue movers, COGS movers, and the
// Revenue − COGS = Gross profit reconciliation with the margin dumbbell —
// the modal's gross-margin movement grid, on the Financials page.
function AccountLensBody({ calc, val, spanMode, helper }: { calc: AccountCalc; val: 'usd' | 'pct'; spanMode: boolean; helper: string }) {
  const dotHi = val === 'usd' ? calc.deltaMax : calc.shareMax
  const rangeLabel = (indices: number[]): string =>
    formatMonth(calc.periods[indices[0]]) + (indices.length > 1 ? `–${formatMonth(calc.periods[indices[indices.length - 1]])}` : '')
  const priorLabel = rangeLabel(calc.priorIndices)
  const nowLabel = rangeLabel(calc.currentIndices)
  const priorHeader = spanMode ? 'First' : 'Prior'
  const nowHeader = spanMode ? 'Latest' : 'Now'
  const groupLabel = (text: string) => (
    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: FAINT, margin: '12px 0 2px' }}>{text}</div>
  )
  const gpDeltaPts = calc.gpPct != null && calc.gpPctPrior != null ? (calc.gpPct - calc.gpPctPrior) * 100 : null
  const gpDeltaAmt = calc.grossProfit != null && calc.grossProfitPrior != null ? calc.grossProfit - calc.grossProfitPrior : null
  // Direction follows the plotted value: GP dollars in $ mode, margin in % mode
  // (they can disagree — margin can rise while GP dollars fall).
  const gpUp = val === 'usd' ? (gpDeltaAmt ?? 0) >= 0 : (calc.gpPct ?? 0) >= (calc.gpPctPrior ?? 0)
  const gpColor = gpUp ? TEAL : RED
  const reconcileCell = (value: number | null, pct: number | null, c: string, muted?: boolean, bold?: boolean) => (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 600 : 400, color: muted ? MUTED : c }}>{fmtMoney(value)}</div>
      {pct != null ? <div style={{ fontSize: 11.5, color: FAINT }}>{fmtPct(pct)}</div> : null}
    </div>
  )
  return (
    <>
      <div style={{ fontSize: 12, color: FAINT, margin: '8px 0 4px' }}>
        {`${helper} · ${nowLabel} vs ${priorLabel} · ${val === 'usd' ? `bars plot the ${cfoCurrencySymbol()} change — ${cfoCurrencySymbol()}0 centered, gains right, declines left` : "dots plot each account's share of revenue"}`}
      </div>
      <div style={gridRow(11.5, FAINT)}>
        <div>Account</div>
        <div style={{ textAlign: 'right' }}>{priorHeader} · {priorLabel}</div>
        {val === 'usd' ? (
          <ScaleRuler lo={-dotHi} hi={dotHi} label={`${cfoCurrencySymbol()} change`} fmt={signedMoneyTick} />
        ) : (
          <ScaleRuler lo={0} hi={dotHi} label="% of revenue" />
        )}
        <div style={{ textAlign: 'right' }}>{nowHeader} · {nowLabel}</div>
      </div>
      {calc.revenue.length ? groupLabel('Revenue accounts') : null}
      {calc.revenue.map((row) => (
        <AccountMovementRow key={row.id} row={row} hi={dotHi} val={val} />
      ))}
      {calc.cogs.length ? groupLabel('COGS accounts') : null}
      {calc.cogs.map((row) => (
        <AccountMovementRow key={row.id} row={row} hi={dotHi} val={val} />
      ))}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', fontSize: 11.5, color: FAINT, padding: '6px 0', borderTop: `1px solid ${HAIR}` }}>
          <div>Reconciliation</div>
          <div style={{ textAlign: 'right' }}>{priorHeader} · {priorLabel}</div>
          <div />
          <div style={{ textAlign: 'right' }}>{nowHeader} · {nowLabel}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: '4px 0' }}>
          <div style={{ color: MUTED, fontSize: 13 }}>Revenue</div>
          {reconcileCell(calc.totalIncomePrior, null, MUTED, true)}
          <div />
          {reconcileCell(calc.totalIncome, null, MUTED)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: '4px 0' }}>
          <div style={{ color: MUTED, fontSize: 13 }}>− COGS</div>
          {reconcileCell(calc.totalCogsPrior != null ? -calc.totalCogsPrior : null, null, MUTED, true)}
          <div />
          {reconcileCell(calc.totalCogs != null ? -calc.totalCogs : null, null, MUTED)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${HAIR}` }}>
          <div style={{ color: INK, fontWeight: 600, fontSize: 14 }}>= Gross profit</div>
          {reconcileCell(calc.grossProfitPrior, calc.gpPctPrior, MUTED, true, true)}
          <div>
            <div style={{ textAlign: 'center', fontSize: 11.5, color: gpColor, marginBottom: 1 }}>
              {(gpDeltaAmt ?? gpDeltaPts ?? 0) >= 0 ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />}{' '}
              {[
                gpDeltaAmt != null ? `${gpDeltaAmt >= 0 ? '+' : '−'}${cfoCurrencySymbol()}${Math.abs(gpDeltaAmt / 1000).toFixed(1)}K` : null,
                gpDeltaPts != null ? `${gpDeltaPts >= 0 ? '+' : '−'}${Math.abs(gpDeltaPts).toFixed(1)}pts` : null,
              ].filter(Boolean).join(' · ') || '—'}
            </div>
            {val === 'usd' ? (
              <DivergingDeltaBar delta={gpDeltaAmt} max={dotHi} improved={gpUp} />
            ) : (
              <Dumbbell prior={calc.gpPctPrior} now={calc.gpPct} up={gpUp} lo={0} hi={dotHi} />
            )}
          </div>
          {reconcileCell(calc.grossProfit, calc.gpPct, gpColor, false, true)}
        </div>
      </div>
    </>
  )
}

// One reconciliation line in the shared 4-column grid (so Prior/Now align with the rows above). The
// bold "= Gross profit" line additionally gets a movement bar (gross margin %) like the items above.
function ReconcileGridRow({ label, prior, now, priorPct, nowPct, calc, view, val = 'pct', scaleLo = 0.3, scaleHi = 0.9, deltaMax = 1, bold }: {
  label: string
  prior: number | null
  now: number | null
  priorPct?: number | null
  nowPct?: number | null
  calc?: Calc
  view?: 'agg' | 'trail'
  val?: 'pct' | 'usd'
  scaleLo?: number
  scaleHi?: number
  deltaMax?: number
  bold?: boolean
}) {
  const withMovement = Boolean(bold && calc && priorPct !== undefined)
  const pp = priorPct ?? null
  const np = nowPct ?? null
  const deltaAmt = prior != null && now != null ? now - prior : null
  // Direction follows the displayed value mode: $ delta in CM $, margin in CM %.
  const up = val === 'usd' && deltaAmt != null ? deltaAmt >= 0 : (np ?? 0) >= (pp ?? 0)
  const color = up ? TEAL : RED
  const deltaPts = pp != null && np != null ? (np - pp) * 100 : null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: bold ? '8px 0' : '4px 0', borderTop: bold ? `1px solid ${HAIR}` : undefined }}>
      <div style={{ color: bold ? INK : MUTED, fontWeight: bold ? 600 : 400, fontSize: bold ? 14 : 13 }}>{label}</div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 600 : 400, color: bold ? MUTED : FAINT }}>{fmtMoney(prior)}</div>
        {withMovement && pp != null ? <div style={{ fontSize: 11.5, color: FAINT }}>{fmtPct(pp)}</div> : null}
      </div>
      <div>
        {withMovement && calc ? (
          view === 'trail' ? (
            <TrailViz trail={calc.gp.trail} gmin={calc.gmin} gmax={calc.gmax} up={up} calc={calc} lo={scaleLo} hi={scaleHi} fallbackPrior={pp} fallbackNow={np} />
          ) : val === 'usd' ? (
            <>
              <div style={{ textAlign: 'center', fontSize: 11.5, color, marginBottom: 1 }}>
                {(deltaAmt ?? 0) >= 0 ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />}{' '}
                {[
                  deltaAmt != null ? `${deltaAmt >= 0 ? '+' : '−'}${cfoCurrencySymbol()}${Math.abs(deltaAmt / 1000).toFixed(1)}K` : null,
                  deltaPts != null ? `${deltaPts >= 0 ? '+' : '−'}${Math.abs(deltaPts).toFixed(1)}pts` : null,
                ].filter(Boolean).join(' · ') || '—'}
              </div>
              <DivergingDeltaBar delta={deltaAmt} max={deltaMax} improved={up} />
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', fontSize: 11.5, color, marginBottom: 1 }}>
                {up ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />} {deltaPts != null ? `${deltaPts >= 0 ? '+' : '−'}${Math.abs(deltaPts).toFixed(1)}pts` : '—'}
              </div>
              <Dumbbell prior={pp} now={np} up={up} lo={scaleLo} hi={scaleHi} />
            </>
          )
        ) : null}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: bold ? 15 : 13, fontWeight: bold ? 600 : 400, color: bold ? (withMovement ? color : INK) : MUTED }}>{fmtMoney(now)}</div>
        {withMovement && np != null ? <div style={{ fontSize: 11.5, color: FAINT }}>{fmtPct(np)}</div> : null}
      </div>
    </div>
  )
}

function LineRow({ line, val, view, calc, scaleLo = 0.3, scaleHi = 0.9, deltaMax = 1, emphasis }: { line: LineCalc; val: 'pct' | 'usd'; view: 'agg' | 'trail'; calc: Calc; scaleLo?: number; scaleHi?: number; deltaMax?: number; emphasis?: boolean }) {
  const up = val === 'pct' ? (line.curPct ?? 0) >= (line.priorPct ?? 0) : (line.curCm ?? 0) >= (line.priorCm ?? 0)
  const color = up ? TEAL : RED
  const delta = val === 'pct'
    ? (line.curPct != null && line.priorPct != null ? (line.curPct - line.priorPct) * 100 : null)
    : (line.curCm != null && line.priorCm != null ? line.curCm - line.priorCm : null)
  const deltaText = delta == null ? '—'
    : val === 'pct' ? `${delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(1)}pts`
      : `${delta >= 0 ? '+' : '−'}${cfoCurrencySymbol()}${Math.abs(delta / 1000).toFixed(1)}K`

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: '11px 0', borderBottom: `${emphasis ? 2 : 1}px solid ${HAIR}` }}>
      <div>
        <div style={{ fontWeight: emphasis ? 600 : 500, fontSize: 14, color: INK }}>{line.name}</div>
        {line.autoDetected ? <div style={{ fontSize: 11, color: FAINT }}>auto-detected</div> : null}
      </div>
      <ValueCell pct={line.priorPct} cm={line.priorCm} val={val} muted />
      <div>
        {view === 'trail' ? (
          <TrailViz trail={line.trail} gmin={calc.gmin} gmax={calc.gmax} up={up} calc={calc} lo={scaleLo} hi={scaleHi} fallbackPrior={line.priorPct} fallbackNow={line.curPct} />
        ) : (
          <>
            <div style={{ textAlign: 'center', fontSize: 11.5, color, marginBottom: 1 }}>
              {up ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />} {deltaText}
            </div>
            {val === 'usd' ? (
              <DivergingDeltaBar
                delta={line.curCm != null && line.priorCm != null ? line.curCm - line.priorCm : null}
                max={deltaMax}
                improved={up}
              />
            ) : (
              <Dumbbell prior={line.priorPct} now={line.curPct} up={up} lo={scaleLo} hi={scaleHi} />
            )}
          </>
        )}
      </div>
      <ValueCell pct={line.curPct} cm={line.curCm} val={val} color={emphasis ? INK : color} bold={emphasis} />
    </div>
  )
}

// The indirect / shared-cost pool, analyzed as a percent of total sales and rolled forward prior → now.
// Lower is better, so the movement coloring is inverted vs. the contribution lines (a rising ratio = red).
function SharedRow({ calc, val, view, scaleLo = 0, scaleHi = 0.5, deltaMax = 1 }: { calc: Calc; val: 'pct' | 'usd'; view: 'agg' | 'trail'; scaleLo?: number; scaleHi?: number; deltaMax?: number }) {
  const s = calc.shared
  const deltaPts = s.curRatio != null && s.priorRatio != null ? (s.curRatio - s.priorRatio) * 100 : null
  const deltaAmt = s.curAmt != null && s.priorAmt != null ? s.curAmt - s.priorAmt : null
  // In CM $ mode this row participates in the shared contribution-change axis,
  // so the displayed signed delta owns the tone: gains/right are teal and
  // declines/left are red. CM % remains a cost-ratio view, where lower is better.
  const dollarGain = (deltaAmt ?? 0) >= 0
  const ratioImproved = (s.curRatio ?? 0) <= (s.priorRatio ?? 0)
  const improved = val === 'usd' ? dollarGain : ratioImproved
  const color = improved ? TEAL : RED
  const deltaText = val === 'pct'
    ? (deltaPts == null ? '—' : `${deltaPts >= 0 ? '+' : '−'}${Math.abs(deltaPts).toFixed(1)}pts`)
    : (deltaAmt == null ? '—' : `${deltaAmt >= 0 ? '+' : '−'}${cfoCurrencySymbol()}${Math.abs(deltaAmt / 1000).toFixed(1)}K`)
  const cell = (rat: number | null, amt: number | null, c: string, muted?: boolean) => {
    const amtText = amt == null ? '—' : `(${fmtMoney(Math.abs(amt))})`
    const big = val === 'pct' ? fmtPct(rat) : amtText
    const small = val === 'pct' ? amtText : fmtPct(rat)
    return (
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: muted ? MUTED : c }}>{big}</div>
        <div style={{ fontSize: 11.5, color: FAINT }}>{small}</div>
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.1fr 2.2fr 1.1fr', gap: 10, alignItems: 'center', padding: '11px 12px', marginTop: 8, background: '#f6f7f9', borderRadius: 8 }}>
      <div>
        <div style={{ fontWeight: 500, fontSize: 14, color: INK, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Lock size={12} aria-hidden="true" /> Indirect / shared costs
        </div>
        <div style={{ fontSize: 11, color: FAINT }}>% of total sales · not allocated to a line</div>
      </div>
      {cell(s.priorRatio, s.priorAmt, MUTED, true)}
      <div>
        {view === 'trail' ? (
          <TrailViz trail={s.trail} gmin={s.gmin} gmax={s.gmax} up={improved} calc={calc} lo={scaleLo} hi={scaleHi} fallbackPrior={s.priorRatio} fallbackNow={s.curRatio} />
        ) : (
          <>
            <div data-contribution-change="shared" style={{ textAlign: 'center', fontSize: 11.5, color, marginBottom: 1 }}>
              {(val === 'usd' ? dollarGain : !ratioImproved) ? <ArrowUpRight size={11} aria-hidden="true" /> : <ArrowDownRight size={11} aria-hidden="true" />} {deltaText}
            </div>
            {val === 'usd' ? (
              <DivergingDeltaBar delta={deltaAmt} max={deltaMax} improved={improved} />
            ) : (
              <Dumbbell prior={s.priorRatio} now={s.curRatio} up={improved} lo={scaleLo} hi={scaleHi} />
            )}
          </>
        )}
      </div>
      {cell(s.curRatio, s.curAmt, color)}
    </div>
  )
}

// Waterfall bridge: start with prior-period gross profit, apply each service line's contribution
// change plus the changes in unpaired revenue and shared COGS, and land on current gross profit.
// The prior/current totals are already part of the governed reconciliation above; keeping the
// waterfall on that same contract prevents a visually plausible but semantically different
// "current values stacked from zero" chart.
function ContributionWaterfall({
  calc,
  priorPeriodLabel,
  currentPeriodLabel,
}: {
  calc: Calc
  priorPeriodLabel: string
  currentPeriodLabel: string
}) {
  if (calc.grossProfitPrior == null || calc.grossProfit == null) return null
  const shortName = (n: string) => n.replace(/\s*(revenue|income)\s*$/i, '').trim() || n
  const steps: Array<{ label: string; value: number; kind: 'add' | 'sub' }> = []
  for (const l of calc.lines) {
    if (l.curCm != null && l.priorCm != null) {
      const value = l.curCm - l.priorCm
      steps.push({ label: shortName(l.name), value, kind: value >= 0 ? 'add' : 'sub' })
    }
  }
  if (calc.otherRevenue != null && calc.otherRevenuePrior != null) {
    const value = calc.otherRevenue - calc.otherRevenuePrior
    if (Math.abs(value) > 0.005) {
      steps.push({ label: 'Other rev', value, kind: value >= 0 ? 'add' : 'sub' })
    }
  }
  if (calc.sharedCogs != null && calc.sharedCogsPrior != null) {
    // COGS is a subtraction in gross profit, so a cost increase is a negative bridge step.
    const value = calc.sharedCogsPrior - calc.sharedCogs
    if (Math.abs(value) > 0.005) {
      steps.push({ label: 'Shared COGS', value, kind: value >= 0 ? 'add' : 'sub' })
    }
  }

  let run = calc.grossProfitPrior
  const stepBars = steps.map((s) => {
    const start = run
    run += s.value
    return { label: s.label, value: s.value, kind: s.kind, start, end: run }
  })
  const bars = [
    {
      label: 'Beginning gross profit',
      value: calc.grossProfitPrior,
      kind: 'total' as const,
      start: 0,
      end: calc.grossProfitPrior,
    },
    ...stepBars,
    {
      label: 'Ending gross profit',
      value: calc.grossProfit,
      kind: 'total' as const,
      start: 0,
      end: calc.grossProfit,
    },
  ]
  const domainMin = Math.min(0, ...bars.flatMap((b) => [b.start, b.end]))
  const domainMax = Math.max(0, ...bars.flatMap((b) => [b.start, b.end]))
  const domainSpan = Math.max(0.0001, domainMax - domainMin)
  const H = 150
  // Reserve dedicated label bands above and below the plot. Without this inset, a total that
  // touches the domain maximum has its value painted at y=0, where it is clipped by the chart.
  const plotTop = 20
  const plotBottom = H - 18
  const plotH = plotBottom - plotTop
  const valueLabelH = 14
  const n = bars.length
  const colW = 100 / n
  const yOf = (v: number) => plotTop + ((domainMax - v) / domainSpan) * plotH
  const fill = (k: string) => (k === 'add' ? '#1D9E75' : k === 'sub' ? '#D85A30' : 'var(--brand, #1b4965)')

  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${HAIR}`, paddingTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>Contribution bridge to gross profit</div>
      <div style={{ fontSize: 11.5, color: FAINT, marginBottom: 10 }}>
        Beginning gross profit · {priorPeriodLabel} → service-line CM changes and shared costs → ending gross profit · {currentPeriodLabel}
      </div>
      <div style={{ position: 'relative', height: H }}>
        <svg viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          {bars.slice(0, -1).map((b, i) => {
            const lvl = yOf(b.end)
            const x1 = (i + 0.78) * colW
            const x2 = (i + 1 + 0.22) * colW
            return <line key={i} x1={x1} y1={lvl} x2={x2} y2={lvl} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
          })}
        </svg>
        {bars.map((b, i) => {
          const topV = Math.max(b.start, b.end)
          const botV = Math.min(b.start, b.end)
          const top = yOf(topV)
          const barH = ((topV - botV) / domainSpan) * plotH
          // Tall bars carry a centered, high-contrast label. Thin positive steps label above and
          // thin negative steps label below, keeping their text away from both the connector and
          // the colored mark. Totals use a slightly roomier threshold.
          const insideThreshold = b.kind === 'total' ? 28 : 20
          const placement = barH >= insideThreshold
            ? 'inside'
            : b.value < 0
              ? 'outside-below'
              : 'outside-above'
          const labelTop = placement === 'inside'
            ? top + Math.max(3, (barH - valueLabelH) / 2)
            : placement === 'outside-below'
              ? Math.min(H - valueLabelH, top + barH + 3)
              : Math.max(0, top - valueLabelH - 3)
          const labelColor = placement === 'inside'
            ? '#fff'
            : b.kind === 'sub'
              ? '#A32D2D'
              : INK
          return (
            <div key={i} style={{ position: 'absolute', left: `${i * colW}%`, width: `${colW}%`, top: 0, height: H }}>
              <div style={{ position: 'absolute', left: '22%', width: '56%', top, height: Math.max(1, barH), background: fill(b.kind), opacity: b.kind === 'total' ? 1 : 0.92, borderRadius: 3 }} />
              <div
                data-waterfall-value-label={b.label}
                data-placement={placement}
                style={{
                  position: 'absolute',
                  zIndex: 1,
                  left: 0,
                  right: 0,
                  top: labelTop,
                  height: valueLabelH,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  fontSize: 10.5,
                  lineHeight: `${valueLabelH}px`,
                  fontWeight: 600,
                  color: labelColor,
                  textShadow: placement === 'inside' ? '0 1px 1px rgba(0,0,0,0.28)' : 'none',
                }}
              >
                <span style={placement === 'inside' ? undefined : { background: 'rgba(255,255,255,0.96)', borderRadius: 3, padding: '1px 4px' }}>
                  {fmtMoney(b.value)}
                </span>
              </div>
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', marginTop: 4 }}>
        {bars.map((b, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: b.kind === 'total' ? INK : FAINT, fontWeight: b.kind === 'total' ? 500 : 400, lineHeight: 1.2 }}>{b.label}</div>
        ))}
      </div>
    </div>
  )
}

function ValueCell({ pct, cm, val, muted, color, bold }: { pct: number | null; cm: number | null; val: 'pct' | 'usd'; muted?: boolean; color?: string; bold?: boolean }) {
  const big = val === 'pct' ? fmtPct(pct) : fmtMoney(cm)
  const small = val === 'pct' ? fmtMoney(cm) : fmtPct(pct)
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 15, fontWeight: bold ? 600 : 500, color: muted ? MUTED : (color || INK) }}>{big}</div>
      <div style={{ fontSize: 11.5, color: FAINT }}>{small}</div>
    </div>
  )
}

// The axis every dumbbell in the section plots on: labeled min/max bounds (and
// a zero tick when the scale crosses zero) rendered once in the column header,
// so dot positions are readable instead of implicit.
function ScaleRuler({ lo, hi, label, fmt }: { lo: number; hi: number; label: string; fmt?: (v: number) => string }) {
  const format = fmt ?? ((v: number) => `${Math.round(v * 100)}%`)
  const zeroPos = lo < 0 && hi > 0 ? ((0 - lo) / (hi - lo)) * 100 : null
  return (
    <div aria-hidden="true" style={{ padding: '0 2px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, fontSize: 10, color: FAINT }}>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{format(lo)}</span>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{format(hi)}</span>
      </div>
      <div style={{ position: 'relative', height: 3, background: '#eceff1', borderRadius: 999, marginTop: 2 }}>
        {zeroPos != null ? (
          <span style={{ position: 'absolute', top: -2, bottom: -2, left: `${zeroPos}%`, width: 1, background: '#c3ccd1' }} />
        ) : null}
      </div>
    </div>
  )
}

// Diverging change bar: $0 anchored at the center of the track, positive
// deltas extend right, negative left, all rows sharing one ±max axis. Color
// still encodes improved/worse (a leftward COGS drop reads teal).
function DivergingDeltaBar({ delta, max, improved }: { delta: number | null; max: number; improved: boolean }) {
  if (delta == null || !Number.isFinite(delta)) {
    return <div style={{ height: 8, margin: '8px 2px', background: '#f1f2f4', borderRadius: 999 }} />
  }
  const color = improved ? TEAL : RED
  const frac = Math.max(-1, Math.min(1, delta / Math.max(max, 0.0001)))
  const end = Math.max(3, Math.min(97, 50 + frac * 50))
  const left = Math.min(50, end)
  const width = Math.max(Math.abs(end - 50), 0.5)
  return (
    <div style={{ position: 'relative', height: 8, background: '#f1f2f4', borderRadius: 999, margin: '8px 2px' }}>
      <div style={{ position: 'absolute', top: -2, bottom: -2, left: '50%', width: 1, background: '#c3ccd1' }} />
      <div style={{ position: 'absolute', top: 0, height: 8, left: `${left}%`, width: `${width}%`, background: color, opacity: 0.55, borderRadius: 999 }} />
      <div style={{ position: 'absolute', top: -3, left: `${end}%`, width: 14, height: 14, marginLeft: -7, borderRadius: '50%', background: color }} />
    </div>
  )
}

function Dumbbell({ prior, now, up, lo = 0.3, hi = 0.9 }: { prior: number | null; now: number | null; up: boolean; lo?: number; hi?: number }) {
  if (prior == null || now == null) return <div style={{ height: 8, margin: '8px 2px', background: '#f1f2f4', borderRadius: 999 }} />
  const pos = (p: number) => Math.max(3, Math.min(97, ((p - lo) / (hi - lo)) * 100))
  const pp = pos(prior)
  const np = pos(now)
  const color = up ? TEAL : RED
  const left = Math.min(pp, np)
  const width = Math.abs(np - pp)
  const zeroPos = lo < 0 && hi > 0 ? ((0 - lo) / (hi - lo)) * 100 : null
  return (
    <div style={{ position: 'relative', height: 8, background: '#f1f2f4', borderRadius: 999, margin: '8px 2px' }}>
      {zeroPos != null ? (
        <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${zeroPos}%`, width: 1, background: '#d3dade' }} />
      ) : null}
      <div style={{ position: 'absolute', top: 0, height: 8, left: `${left}%`, width: `${width}%`, background: color, opacity: 0.55, borderRadius: 999 }} />
      <div style={{ position: 'absolute', top: -3, left: `${pp}%`, width: 14, height: 14, marginLeft: -7, borderRadius: '50%', background: '#fff', border: `2px solid ${FAINT}` }} />
      <div style={{ position: 'absolute', top: -3, left: `${np}%`, width: 14, height: 14, marginLeft: -7, borderRadius: '50%', background: color }} />
    </div>
  )
}

function TrailViz({ trail, gmin, gmax, up, calc, lo = 0.3, hi = 0.9, fallbackPrior, fallbackNow }: {
  trail: Array<{ idx: number; pct: number | null }>
  gmin: number
  gmax: number
  up: boolean
  calc: Calc
  lo?: number
  hi?: number
  fallbackPrior: number | null
  fallbackNow: number | null
}) {
  const defined = trail.filter((p) => p.pct != null)
  if (defined.length < 2) return <Dumbbell prior={fallbackPrior} now={fallbackNow} up={up} lo={lo} hi={hi} />
  const color = up ? TEAL : RED
  const n = trail.length
  const xs = (j: number) => (n <= 1 ? 50 : 4 + (j / (n - 1)) * 92)
  const ys = (v: number) => 6 + (1 - (v - gmin) / (gmax - gmin || 1)) * 88
  const priorSet = new Set(calc.priorIndices)
  const curSet = new Set(calc.currentIndices)
  const poly = trail
    .map((p, j) => (p.pct == null ? null : `${xs(j).toFixed(2)},${ys(p.pct).toFixed(2)}`))
    .filter(Boolean)
    .join(' ')
  const boundaryPos = trail.findIndex((p) => p.idx === calc.currentIndices[0])
  const bx = boundaryPos > 0 ? (xs(boundaryPos - 1) + xs(boundaryPos)) / 2 : 0
  return (
    <div>
      <div style={{ position: 'relative', height: 46, margin: '2px 0' }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <polyline points={poly} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
        {bx > 0 ? <div style={{ position: 'absolute', left: `${bx}%`, top: 0, bottom: 0, borderLeft: `1px dashed ${FAINT}` }} /> : null}
        {trail.map((p, j) => {
          if (p.pct == null) return null
          const x = xs(j)
          const y = ys(p.pct)
          const title = `${formatMonth(calc.periods[p.idx])}: ${fmtPct(p.pct)}`
          if (priorSet.has(p.idx)) return <span key={j} title={title} style={dot(x, y, 10, '#fff', `2px solid ${FAINT}`)} />
          if (curSet.has(p.idx)) return <span key={j} title={title} style={dot(x, y, 11, color)} />
          return <span key={j} title={title} style={dot(x, y, 5, '#cbd5e1')} />
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: FAINT, marginTop: -1 }}>
        <span>{formatMonth(calc.periods[calc.trailRange.start])}</span>
        <span>{formatMonth(calc.periods[calc.trailRange.end])}</span>
      </div>
    </div>
  )
}

function dot(x: number, y: number, size: number, bg: string, border?: string): CSSProperties {
  return { position: 'absolute', left: `${x}%`, top: `${y}%`, width: size, height: size, transform: 'translate(-50%, -50%)', borderRadius: '50%', background: bg, border, display: 'block' }
}

const panelShell: CSSProperties = {
  marginTop: 14,
  background: '#fff',
  border: `1px solid ${HAIR}`,
  borderRadius: 12,
  padding: '14px 16px',
}

export default ServiceLineContributionPanel
